import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { createAuth } from '../Helpers';
import moment from 'moment';

export const reducerName = 'resourceState';

export const getAllUnits = createAsyncThunk(
    'resourceState/getAllUnits',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unit`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const getAllBookings = createAsyncThunk(
    'resourceState/getAllBookings',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/unitbooking`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const createBooking = createAsyncThunk(
    'resourceState/createBooking',
    async ({ unitId, teamId, personId, startTime, endTime }, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/unitbooking`,
                data: {
                    unitId,
                    teamId,
                    personId,
                    startTime: moment(startTime).format('YYYY-MM-DD HH:mm'),
                    endTime: moment(endTime).format('YYYY-MM-DD HH:mm'),
                },
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            if (error.response?.data === 'double_booking') {
                return rejectWithValue({ doubleBook: true });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState = {
    units: [],
    allUnits: [],
    bookings: [],
    createBooking: {
        unitId: '',
        teamId: '',
        personId: null,
        startTime: moment().valueOf(),
        endTime: moment().valueOf()
    },
    doubleBook: false,
    unitMenuId: false,
    showTeamMenu: false,
    showTimeMenu: false,
    loading: { getUnits: false, getBookings: false, createBooking: false },
    activeCategory: undefined,
    showCategories: true,
    categories: [],
    error: null,
};

const resourceSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        setBookingField: (state, action) => {
            const { field, value } = action.payload;
            state.createBooking[field] = value;
        },
        setTeamAndBook: (state, action) => {
            const { teamId, personId } = action.payload;
            state.createBooking.teamId = teamId;
            state.createBooking.personId = personId;
        },
        unitMenu: (state, action) => {
            const { unitId, visible } = action.payload;
            state.unitMenuId = visible ? unitId : false;
        },
        showTeamMenu: (state, action) => {
            state.showTeamMenu = action.payload;
        },
        showTimeMenu: (state, action) => {
            state.showTimeMenu = action.payload;
            state.doubleBook = false;
        },
        setToCategoryView: (state) => {
            state.showCategories = true;
            state.activeCategory = undefined;
            state.units = state.allUnits;
        },
        setToUnitView: (state, action) => {
            state.showCategories = false;
            state.activeCategory = action.payload;
            // Filter units by category
            const category = action.payload;
            state.units = state.allUnits.filter(u => 
                u.category === category || (category === "Andre" && u.category === null)
            );
        },
        setCategoriesByUnits: (state) => {
            const categories = [...new Set(state.units.map(u => u.category || "Andre"))];
            state.categories = categories.sort((a, b) => a.localeCompare(b));
        },
        resetDoubleBookError: (state) => {
            state.doubleBook = false;
        },
    },
    extraReducers: (builder) => {
        // getAllUnits
        builder
            .addCase(getAllUnits.pending, (state) => {
                state.loading.getUnits = true;
                state.error = null;
            })
            .addCase(getAllUnits.fulfilled, (state, action) => {
                state.loading.getUnits = false;
                state.units = action.payload;
                state.allUnits = action.payload;
                // Set categories automatically when units are loaded
                state.categories = [...new Set(action.payload.map(u => u.category || "Andre"))];
            })
            .addCase(getAllUnits.rejected, (state, action) => {
                state.loading.getUnits = false;
                state.error = action.payload || 'An error occurred';
            });

        // getAllBookings
        builder
            .addCase(getAllBookings.pending, (state) => {
                state.loading.getBookings = true;
                state.error = null;
            })
            .addCase(getAllBookings.fulfilled, (state, action) => {
                state.loading.getBookings = false;
                state.bookings = action.payload;
            })
            .addCase(getAllBookings.rejected, (state, action) => {
                state.loading.getBookings = false;
                state.error = action.payload || 'An error occurred';
            });

        // createBooking
        builder
            .addCase(createBooking.pending, (state) => {
                state.loading.createBooking = true;
                state.doubleBook = false;
                state.error = null;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.loading.createBooking = false;
                state.showTeamMenu = false;
                state.unitMenuId = false;
                state.bookings.push(action.payload);
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.loading.createBooking = false;
                if (action.payload?.doubleBook) {
                    state.doubleBook = true;
                    state.showTeamMenu = false;
                    state.showTimeMenu = true;
                } else {
                    state.error = action.payload || 'An error occurred';
                }
            });
    },
});

export const {
    setBookingField,
    setTeamAndBook,
    unitMenu,
    showTeamMenu,
    showTimeMenu,
    setToCategoryView,
    setToUnitView,
    setCategoriesByUnits,
    resetDoubleBookError,
} = resourceSlice.actions;

export default resourceSlice.reducer;

export const reducer = resourceSlice.reducer;

export const setTeamAndBookThunk = (teamId, personId) => (dispatch, getState) => {
    dispatch(setTeamAndBook({ teamId, personId }));
    const state = getState()[reducerName];
    const { unitId, startTime, endTime } = state.createBooking;
    dispatch(createBooking({ unitId, teamId, personId, startTime, endTime }));
};